import { __assign, __makeTemplateObject, __rest } from "tslib";
import React, { forwardRef, useImperativeHandle, useMemo, useRef, } from 'react';
import { isFragment } from 'react-is';
import { combineProps, getScrollbarSize, isRcElement, logInDev, styled, useEventCallback, useForkRef, useHiddenTabindex, useKeyboardMoveFocus, useMountState, useOnlyOneFocusable, useRcPortalWindowContext, useTheme, useThemeProps, } from '../../foundation';
import { RcList } from '../List/List/List';
import { isOutOfRange, modifyVlScrollerStyle, useDynamicHeight, useHighlightScroll, Virtuoso, } from '../Virtuoso';
import { RcVisuallyHidden } from '../VisuallyHidden';
import { StyledMenuListPadding } from './styles';
var _RcVirtualizedMenuList = forwardRef(function (inProps, ref) {
    var props = useThemeProps({
        props: inProps,
        name: 'RcVirtualizedMenuList',
    });
    var action = props.action, disablePadding = props.disablePadding, _a = props.autoFocus, autoFocus = _a === void 0 ? false : _a, VirtuosoProps = props.VirtuosoProps, _b = props.autoFocusItem, autoFocusItem = _b === void 0 ? false : _b, childrenProp = props.children, className = props.className, classes = props.classes, _c = props.disabledItemsFocusable, disabledItemsFocusable = _c === void 0 ? false : _c, _d = props.disableListWrap, disableListWrap = _d === void 0 ? false : _d, _e = props.variant, variant = _e === void 0 ? 'selectedMenu' : _e, _f = props.maxHeight, maxHeight = _f === void 0 ? '100%' : _f, autoClose = props.autoClose, position = props.position, onClose = props.onClose, rest = __rest(props, ["action", "disablePadding", "autoFocus", "VirtuosoProps", "autoFocusItem", "children", "className", "classes", "disabledItemsFocusable", "disableListWrap", "variant", "maxHeight", "autoClose", "position", "onClose"]);
    var theme = useTheme();
    var document = useRcPortalWindowContext().document;
    var vlRef = useRef(null);
    var innerListRef = React.useRef(null);
    var handleRef = useForkRef(innerListRef, ref);
    var rangeChangedRef = useRef({ startIndex: 0, endIndex: 0 });
    var isMountedRef = useMountState();
    var itemRenderedDescriptorRef = useRef(null);
    var hasSearchText = false;
    /**
     * the index of the item should receive focus
     * in a `variant="selectedMenu"` it's the first `selected` item
     * otherwise it's the very first item.
     */
    var activeItemIndex = -1;
    // since we inject focus related props into children we have to do a lookahead
    // to check if there is a `selected` item. We're looking for the last `selected`
    // item and use the first valid item as a fallback
    var items = React.Children.map(childrenProp, function (child, index) {
        if (!React.isValidElement(child)) {
            return;
        }
        if (process.env.NODE_ENV !== 'production' && isFragment(child)) {
            logInDev({
                component: 'RcVirtualizedMenuList',
                message: [
                    "Material-UI: The Menu component doesn't accept a Fragment as a child.",
                    'Consider providing an array instead.',
                ].join('\n'),
            });
        }
        if (!child.props.disabled) {
            if (variant === 'selectedMenu' && child.props.selected) {
                activeItemIndex = index;
            }
            else if (activeItemIndex === -1) {
                activeItemIndex = index;
            }
        }
        if (child.props['data-search-text'] !== undefined) {
            hasSearchText = true;
        }
        return child;
    });
    var itemCount = items.length;
    var focusedIndexRef = useRef(activeItemIndex);
    focusedIndexRef.current = activeItemIndex;
    var onContainerHeightChange = useEventCallback(function (changeHeight) {
        var scroller = scrollerRef.current;
        if (scroller === null || scroller === void 0 ? void 0 : scroller.style) {
            scroller.style.height = changeHeight + "px";
        }
    });
    var _g = useDynamicHeight({
        itemCount: itemCount,
        maxContainerHeight: maxHeight,
        onContainerHeightChange: onContainerHeightChange,
    }), totalListHeightChanged = _g.totalListHeightChanged, style = _g.style, containerHeighRef = _g.containerHeighRef;
    var modifyScrollPosition = function () { var _a; return (_a = vlRef.current) === null || _a === void 0 ? void 0 : _a.scrollBy({ top: -8 }); };
    var _h = useHighlightScroll({
        containerHeighRef: containerHeighRef,
        scrollToIndex: function (location) {
            var _a;
            (_a = vlRef.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex(location);
            if (location.index === 0)
                modifyScrollPosition();
        },
    }), scrollerRef = _h.scrollerRef, scrollerRefFn = _h.scrollerRefFn, itemsRendered = _h.itemsRendered, scrollToHighlightedIndex = _h.scrollToHighlightedIndex;
    var _j = useOnlyOneFocusable({
        focusedIndexRef: focusedIndexRef,
        containerRef: innerListRef,
    }), focusIndex = _j.focusIndex, getItemProps = _j.getItemProps;
    var focusItemByIndex = function (currentfocusedIndex, targetfocusedIndex) {
        scrollToHighlightedIndex(currentfocusedIndex === null ? 0 : currentfocusedIndex, targetfocusedIndex);
        if (isOutOfRange(targetfocusedIndex, rangeChangedRef.current)) {
            new Promise(function (resolve) {
                itemRenderedDescriptorRef.current = {
                    resolve: resolve,
                    index: targetfocusedIndex,
                };
            }).then(function () {
                focusIndex(targetfocusedIndex);
            });
        }
        else {
            focusIndex(targetfocusedIndex);
        }
    };
    var _k = useKeyboardMoveFocus({
        options: items,
        focusedIndexRef: focusedIndexRef,
        infinite: !disableListWrap,
        onFocusedIndexChange: function (event, toIndex) {
            focusItemByIndex(focusedIndexRef.current, toIndex);
            event.preventDefault();
        },
        getOptionDisabled: function (child) {
            return (!disabledItemsFocusable &&
                (child.props.disabled ||
                    child.props['aria-disabled'] ||
                    child.props['data-disabled-focus'] ||
                    // if that item is divider also not allow to select
                    isRcElement(child, ['RcDivider', 'RcVirtualizedDivider'])));
        },
        // * only when any children have search-text field need search
        getOptionSearchText: hasSearchText
            ? function (child) {
                return child.props['data-search-text'];
            }
            : undefined,
    }), onKeyFocusedIndexHandle = _k.onKeyFocusedIndexHandle, getNextFocusableOption = _k.getNextFocusableOption;
    var hiddenRef = useRef(null);
    var events = useHiddenTabindex(hiddenRef);
    var onMounted = useEventCallback(function () {
        // * when index change forceUpdate for keep inner and outside state sync
        var _a;
        if (autoFocus) {
            (_a = innerListRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        if (disabledItemsFocusable) {
            focusItemByIndex(null, 0);
        }
        else {
            // * when init find available default index
            var initFocusedIndex_1 = getNextFocusableOption();
            if (autoFocusItem) {
                // * timeout for wait vl render complete
                setTimeout(function () {
                    focusItemByIndex(null, initFocusedIndex_1);
                }, 0);
            }
        }
    });
    useImperativeHandle(action, function () { return ({
        scrollIntoViewAndFocus: onMounted,
        adjustStyleForScrollbar: function () {
            var listElm = innerListRef.current;
            var scrollElm = scrollerRef.current;
            if (typeof maxHeight === 'number' &&
                scrollElm.clientHeight === maxHeight) {
                var scrollbarSize = getScrollbarSize() + "px";
                var styleDirection = theme.direction === 'rtl' ? 'paddingLeft' : 'paddingRight';
                listElm.style[styleDirection] = scrollbarSize;
                listElm.style.width = "calc(100% + " + scrollbarSize + ")";
                scrollElm.style.overflowX = 'hidden';
            }
        },
        adjustContainerHeight: function (value) {
            containerHeighRef.current = value;
            onContainerHeightChange(value);
        },
    }); });
    var components = useMemo(function () { return ({
        Header: disablePadding
            ? undefined
            : StyledMenuListPadding,
        Footer: disablePadding
            ? undefined
            : StyledMenuListPadding,
        List: React.forwardRef(function (_a, listRef) {
            var children = _a.children, listRest = __rest(_a, ["children"]);
            var toRef = useForkRef(handleRef, listRef);
            return (React.createElement(RcList, __assign({ role: "menu", ref: toRef }, combineProps(listRest, rest)), children));
        }),
        Item: function (_a) {
            var child = _a.children, itemRest = __rest(_a, ["children"]);
            var index = itemRest['data-index'];
            return React.cloneElement(child, __assign(__assign(__assign({}, itemRest), { 'aria-posinset': index, 'aria-setsize': itemCount }), getItemProps(index)));
        },
    }); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    var combine = combineProps(__assign({ tabIndex: -1, scrollerRef: function (scrollElm) {
            scrollerRefFn(scrollElm);
            modifyVlScrollerStyle(scrollElm, position);
            // * when not have scrollerRef mean that is first render
            if (!isMountedRef.current) {
                onMounted();
            }
        }, onKeyDown: onKeyFocusedIndexHandle, itemsRendered: itemsRendered, rangeChanged: function (range) {
            rangeChangedRef.current = range;
            var focusedIndex = focusedIndexRef.current;
            var listElm = scrollerRef.current;
            if (listElm &&
                document.activeElement &&
                document.activeElement !== listElm &&
                listElm.contains(document.activeElement) &&
                isOutOfRange(focusedIndex, range)) {
                listElm.focus();
            }
            var itemRenderedDescriptor = itemRenderedDescriptorRef.current;
            if (itemRenderedDescriptor &&
                !isOutOfRange(itemRenderedDescriptor.index, rangeChangedRef.current)) {
                itemRenderedDescriptor.resolve();
                itemRenderedDescriptorRef.current = null;
            }
        }, totalListHeightChanged: totalListHeightChanged,
        style: style }, events), VirtuosoProps);
    return (React.createElement(React.Fragment, null,
        React.createElement(RcVisuallyHidden, { ref: hiddenRef, onFocus: function () {
                var _a;
                focusItemByIndex(((_a = rangeChangedRef.current) === null || _a === void 0 ? void 0 : _a.startIndex) || 0, focusedIndexRef.current);
            } }),
        React.createElement(Virtuoso, __assign({ ref: vlRef, data: items, itemContent: function (index, data) { return data; }, totalCount: itemCount, components: components }, combine))));
});
var RcVirtualizedMenuList = styled(_RcVirtualizedMenuList)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
RcVirtualizedMenuList.defaultProps = {};
RcVirtualizedMenuList.displayName = 'RcVirtualizedMenuList';
export { RcVirtualizedMenuList };
var templateObject_1;
